import tipsApi from "@/request/tips.js"
import menuApi from "@/request/menu.js"
import subscriptionApi from "@/request/subscription.js"

class Service {
    constructor() {
        this.$tips = tipsApi
        this.$menu = menuApi
        this.$subscription = subscriptionApi
    }

    appAuth(data) {
        return this.$menu.post("/auth/app-auth", data)
    }

    authMe(headers) {
        return this.$menu.get("/auth/me", { headers })
    }

    createDemoOrder(params, headers) {
        return this.$tips.get(`/store/create-demo-order?initData=${params}`, { headers })
    }

    getDemoOrders(params, headers) {
      return this.$tips.get(`/store/get-demo-orders?initData=${params}`, { headers })
    }

    getDemoOrder(terminal, order) {
      return this.$tips.get(`/store/terminal/${terminal}/order/${order}`);
    }

    toggleDemoOrderStatus(order, params) {
      return this.$tips.get(`/store/toggle-demo-order-status?uuid=${order}&initData=${params}`);
    }

    toggleDemoOrderPayment(order, params) {
      return this.$tips.get(`/store/toggle-demo-order-payment?uuid=${order}&initData=${params}`);
    }

    addCard(headers, params) {
        return this.$tips.post("/payment/card", params, { headers })
    }

    confirmCard(headers, token, otp) {
        return this.$tips.put(`/payment/card/${token}`, { otp }, { headers })
    }

    saveCard(headers, card) {
      return this.$tips.post(`/menu/profile/save-card`, { card }, { headers })
    }

    deleteCard(headers, token, pan) {
        return this.$menu.delete(`/profile/delete-card/${token}/${pan}`, { headers })
    }

    paymentDetails(id, params) {
        return this.$tips.get(`/payment/details/${id}`, { params })
    }

    paymentSettings(store) {
        return this.$tips.get(`/payment/store/${store}`)
    }

    paymentAgr(params) {
        return this.$tips.post("/payment/agr", params)
    }

    paymentAgrCheck(uuid) {
        return this.$tips.get(`/payment/agr/${uuid}`)
    }

    paymentAgrConfirm(uuid, otp) {
        return this.$tips.put(`/payment/agr/${uuid}`, { otp })
    }

    applePayValidateMerchant(validationURL) {
        return this.$tips.post("/payment/apple-pay-validate-merchant", { validationURL })
    }

    getPaymentSystems() {
        return this.$menu.get("/profile/payment-systems");
    }

    savePsSequence(headers, systems) {
        return this.$menu.put("/profile/save-ps-sequence", { systems }, { headers });
    }

    saveCardSequence(headers, cards) {
        return this.$menu.put("/profile/save-card-sequence", { cards }, { headers });
    }

    getEmployee(id) {
        return this.$tips.get(`/employee/${id}`);
    }

    reviewCreate(payload) {
        return this.$tips.post("/review", payload);
    }

    reviewCopy(uuid) {
        return this.$tips.post(`/review-copy/${uuid}`);
    }

    reviewConfirm(id, payload) {
        return this.$tips.post(`/review-confirm/${id}`, payload);
    }

    reviewTips(id, payload) {
        return this.$tips.post(`/review-tips/${id}`, payload);
    }

    reviewStore(store, order, payload) {
        return this.$tips.post(`/review-store/${store}/${order}`, payload);
    }

    paymentWithoutOrder(terminal, payload) {
        return this.$tips.post(`/payment/static/terminal/${terminal}`, payload);
    }

    paymentCreate(store, order, payload) {
        return this.$tips.post(`/payment/${store}/${order}`, payload);
    }

    paymentConfirm(id, payload) {
        return this.$tips.post(`/payment-confirm/${id}`, payload);
    }

    getQr(id) {
        return this.$tips.get(`/store/qr?id=${id}`)
    }

    qrPaymentCreate(session, card) {
        return this.$tips.get(`/store/qr-payment?session=${session}&pan=${card.pan}&expiry=${card.expiry}`)
    }

    qrPaymentConfirm(id, otp) {
        return this.$tips.get(`/store/qr-confirm?id=${id}&otp=${otp}`)
    }

    qrPaymentCheck(id) {
        return this.$tips.get(`/store/qr-check?id=${id}`)
    }

    getReview(employee, review) {
        return this.$tips.get(`/employee/${employee}/review/${review}`);
    }

    getTerminal(store) {
        return this.$tips.get(`/terminal/${store}`);
    }

    posInfo(pos, device) {
        return this.$tips.get(`/store/${pos}/${device}`)
    }

    posInvoiceDetails(pos, device, invoice, employee) {
        const payload = {
            employee_uuid: employee
        }
        const params = new URLSearchParams({
            ...payload
        })
        if (!employee || employee == 0) {
            params.delete('employee_uuid')
        }
        return this.$tips.get(`/store/${pos}/${device}/invoice/${invoice}`, { params })
    }

    invoiceDetails(store, cashdesk, order, employee, table, details) {
        const payload = {
            employee_uuid: employee,
            cashdesk_id: cashdesk,
            order_guid: order,
            table_id: table,
            details: details ? 1 : 0,
        }
        const params = new URLSearchParams({
            ...payload,
        })
        if (!order) {
            params.delete('order_guid')
        }
        if (!cashdesk || cashdesk == 0) {
            params.delete('cashdesk_id')
        }
        if (!table) {
            params.delete('table_id')
        }
        if (!employee || employee == 0) {
            params.delete('employee_uuid')
        }
        return this.$tips.get(`/store/${store}/order-details`, { params });
    }

    storeEmployees(store, headers) {
        return this.$tips.get(`/store/employees?store=${store}`, { headers });
    }

    receiptStatus(uuid) {
        return this.$tips.get(`/review/receipt/${uuid}`);
    }

    paymentStatus(id, system) {
        const params = new URLSearchParams({ id, system })
        return this.$tips.get(`/review/status?${params.toString()}`);
    }

    fastPayStatusByUuid(terminal, guid, uuid) {
        const params = new URLSearchParams({ terminal, guid, uuid })
        return this.$tips.get(`/review/status-by-uuid?${params.toString()}`);
    }

    paymentStatusByGuid(terminal, guid) {
        const params = new URLSearchParams({ terminal, guid })
        return this.$tips.get(`/review/status-by-guid?${params.toString()}`);
    }

    orderWithDetails(store, order) {
        return this.$tips.get(`/store/${store}/order-with-details/${order}`);
    }

    getOrder(terminal, order) {
        return this.$tips.get(`/store/terminal/${terminal}/order/${order}`);
    }

    getCurrentOrder(terminal) {
        return this.$tips.get(`/store/terminal/${terminal}/current-order`);
    }

    getInvoice(uuid) {
        return this.$tips.get(`/payment/agr/${uuid}`);
    }

    getLastInvoice(uuid) {
        return this.$tips.get(`/payment/store/${uuid}/last`);
    }

    authOtp(phone) {
        return this.$menu.post('/auth/otp', { phone })
    }

    authVerify(phone, otp) {
        return this.$menu.post("/auth/verify", { phone, otp })
    }

    authRefresh(refresh) {
        return this.$menu.post("/auth/refresh", { refresh })
    }

    saveAccountInfo(id, params, headers) {
        return this.$menu.post(`/profile/save/${id}`, params, { headers })
    }

    saveProfileAvatar(params, headers) {
        return this.$menu.patch('/profile/avatar', params, { headers })
    }

    getTransactions(params, headers) {
        return this.$menu.get(`/profile/payments?page[number]=${params.skip}&page[size]=${params.offset}`, { headers })
    }

    getOrders(params, headers) {
        return this.$menu.get(`/profile/orders?page[number]=${params.skip}&page[size]=${params.offset}`, { headers })
    }

    orderView(params, headers) {
      return this.$tips.get(`/menu/profile/order-view?store=${params.store}&guid=${params.guid}&table=${params.table}`,  { headers });
    }

    getReservations(params, headers) {
        return this.$tips.get(`/menu/profile/reservations?skip=${params.skip}&offset=${params.offset}`,  { headers });
    }

    deleteAddress(id, headers) {
        return this.$tips.post(`/menu/profile/delete-address?id=${id}`, {}, { headers });
    }

    editPhone(params, headers) {
        return this.$tips.post(`/menu/profile/add-phone?phone=${params.phone}`, {}, { headers })
    }

    getMarketplaceStores(params, headers) {
        return this.$menu.get("/marketplace", { params, headers });
        // return this.$tips.get("/qr-menu/marketplace", { params, headers });
    }

    getMarketplaceStore(store, params, headers) {
        return this.$menu.get(`/marketplace/store/${store}`, { params, headers });
    }

    getMarketPlaceStoreList(params, headers) {
        return this.$tips.get('/qr-menu/stores', { params, headers })
    }

    getTaxiInfo(params) {
        return this.$menu.get('/marketplace/taxi-info', { params });
        // return this.$tips.get('/qr-menu/taxi-info', { params });
    }

    getTables(brand, headers) {
        return this.$menu.get(`/tables/${brand}`, { headers })
        // return this.$tips.get(`/qr-menu/tables?brand=${brand}`, { headers })
    }

    getProducts(type, brand_id, store_id, table, coordinates, headers) {
        const params = { type, brand_id, store_id, table, coordinates }
        return this.$menu.get('/list', { params, headers });
        // return this.$tips.get('/qr-menu/list', { params, headers });
    }

    call(store, table, type, headers) {
        const params = new URLSearchParams({ store, table, type });
        return this.$tips.get("/menu/order/call", { params, headers });
    }

    getOrderByTable(table, headers) {
        return this.$tips.get(`/menu/order/view-order?table=${table}`, {headers})
    }

    getOrderByGuid(store, guid, headers) {
        return this.$tips.get(`/menu/order/view-order?store=${store}&guid=${guid}`, {headers})
    }

    makeOrder(order, headers) {
        return this.$tips.post("/menu/order/create-order", order, { headers });
    }

    editOrder(order, headers) {
        return this.$tips.post("/menu/order/add-order", order, { headers });
    }

    payOrder(order, headers) {
        return this.$tips.post(`/menu/order/close?id=${order}`, {}, { headers });
    }

    orderDeleteByGuid(order, headers) {
        return this.$tips.post(`/menu/order/delete-by-guid?guid=${order}`, null, { headers });
    }

    // orderDeleteByTable(table, headers) {
    //     return this.$tips.post(`/menu/order/delete-by-table?table=${table}`, null, { headers });
    // }

    orderHide(order, headers) {
        return this.$tips.post(`/menu/order/hide?uid=${order}`, null, { headers });
    }

    orderHideByGuid(store, guid, headers) {
        return this.$tips.post(`/menu/order/hide-by-guid?guid=${guid}&store=${store}`, null, { headers })
    }

    yandexPromo(type) {
        return this.$tips.get(`/yandex/promo/${type}`)
    }

    subscriptionTariffs(id) {
        return this.$subscription.get(`/tariffs/${id}`)
    }

    createSubscription(params, body) {
        const urlParams = new URLSearchParams(params)
        return this.$subscription.post(`/subscribers?${urlParams.toString()}`, body)
    }

    confirmSubscription(id, body) {
        return this.$subscription.post(`/subscribers/${id}/confirm`, body)
    }

    updateSubscription(id, params, body) {
        const urlParams = new URLSearchParams(params)
        return this.$subscription.put(`/subscribers/${id}?${urlParams.toString()}`, body)
    }

    cancelSubscription(id, params, body) {
        const urlParams = new URLSearchParams(params)
        return this.$subscription.delete(`/subscribers/${id}?${urlParams.toString()}`, body)
    }
}

export default new Service()
